import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import COLORS from '../../../constants/colors';
import { SectionCard } from '../../../components/Section';
import { PlanRulesDraft, PlanRulesView } from '../../../types/planRules';
import { WEIGHTS_SHORT_LABELS } from './premium.constants';
import { geoText, isFreePlan, limitText, planLabel, ruleKey } from './premium.helpers';
import { PremiumSection } from './premiumTabs';

interface ComparePlansProps {
  draft: PlanRulesDraft;
  services: PlanRulesView['services'];
  productName: (id: number) => string;
  /** Keys of draft changes, to mark changed cells. */
  changedKeys: Set<string>;
  onOpen: (section: PremiumSection, groupId?: number) => void;
}

const valueColor = (value: number | null | undefined) => {
  if (value === null) {
    return COLORS.SECONDARY_GREEN;
  }

  return value === 0 ? COLORS.LIGHT_GRAY : COLORS.MAIN_DARK;
};

/** Home of the tab: what each plan allows, side by side, like a pricing table. Click a value to edit it. */
export function ComparePlans({ draft, services, productName, changedKeys, onOpen }: ComparePlansProps) {
  const columns = draft.plans.length + 1;

  const valueCell = (text: string, color: string, changed: boolean, section: PremiumSection, groupId?: number) => (
    <TableCell>
      <ButtonBase
        onClick={() => onOpen(section, groupId)}
        title="Edit"
        sx={{
          px: '8px',
          py: '2px',
          mx: '-8px',
          borderRadius: '6px',
          color,
          fontWeight: color === COLORS.SECONDARY_GREEN ? 600 : 400,
          fontFamily: 'inherit',
          fontSize: 'inherit',
          gap: '6px',
          ...(changed && { bgcolor: alpha(COLORS.ORANGE, 0.12), boxShadow: `inset 3px 0 0 ${COLORS.ORANGE}` }),
          '&:hover': { bgcolor: alpha(COLORS.PRIMARY_BLUE, 0.08) },
        }}
      >
        {changed && <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: COLORS.ORANGE }} />}
        {text}
      </ButtonBase>
    </TableCell>
  );

  const sectionRow = (title: string, detail?: string) => (
    <TableRow>
      <TableCell colSpan={columns} sx={{ bgcolor: alpha(COLORS.PRIMARY_BLUE, 0.05), py: '8px' }}>
        <Typography variant="overline" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
          {title}
        </Typography>
        {detail && (
          <Typography component="span" variant="caption" sx={{ ml: 1, color: COLORS.LIGHT_GRAY }}>
            {detail}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <SectionCard
      title="Compare plans"
      description="What each plan allows, side by side. Click any value to change it."
      disablePadding
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="Compare plans">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '32%' }}>Rule</TableCell>
              {draft.plans.map((plan) => (
                <TableCell key={plan.id}>
                  <Typography sx={{ fontWeight: 700 }}>{planLabel(plan)}</Typography>
                  <Typography variant="caption" sx={{ color: COLORS.LIGHT_GRAY }}>
                    {isFreePlan(plan) ? 'Free' : 'Paid'}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sectionRow('Plan totals')}
            <TableRow>
              <TableCell>Portfolios a user can open</TableCell>
              {draft.plans.map((plan) => (
                <React.Fragment key={plan.id}>
                  {valueCell(
                    limitText(plan.portfolioLimit, 'portfolio'),
                    valueColor(plan.portfolioLimit),
                    changedKeys.has(`plan.${plan.id}.portfolioLimit`),
                    'totals',
                  )}
                </React.Fragment>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Saved recommendations</TableCell>
              {draft.plans.map((plan) => (
                <React.Fragment key={plan.id}>
                  {valueCell(
                    `${limitText(plan.recommendationLimit, 'recommendation')}${
                      plan.recommendationCappedByPortfolios ? ' *' : ''
                    }`,
                    valueColor(plan.recommendationLimit),
                    changedKeys.has(`plan.${plan.id}.recommendationLimit`),
                    'totals',
                  )}
                </React.Fragment>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Management fee</TableCell>
              {draft.plans.map((plan) => (
                <React.Fragment key={plan.id}>
                  {valueCell(
                    plan.managementFee ? 'Charged' : 'No fee',
                    COLORS.MAIN_DARK,
                    changedKeys.has(`plan.${plan.id}.managementFee`),
                    'totals',
                  )}
                </React.Fragment>
              ))}
            </TableRow>

            {sectionRow('Robo Advice', 'portfolios opened from the survey')}
            <TableRow>
              <TableCell>Portfolios</TableCell>
              {draft.plans.map((plan) => (
                <React.Fragment key={plan.id}>
                  {valueCell(
                    limitText(plan.roboAdvicePortfolioLimit, 'portfolio'),
                    valueColor(plan.roboAdvicePortfolioLimit),
                    changedKeys.has(`plan.${plan.id}.roboAdvicePortfolioLimit`),
                    'robo',
                  )}
                </React.Fragment>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Fund weights</TableCell>
              {draft.plans.map((plan) => (
                <React.Fragment key={plan.id}>
                  {valueCell(
                    WEIGHTS_SHORT_LABELS[plan.roboAdviceWeightsRule],
                    COLORS.MAIN_DARK,
                    changedKeys.has(`plan.${plan.id}.roboAdviceWeightsRule`),
                    'robo',
                  )}
                </React.Fragment>
              ))}
            </TableRow>

            {draft.groups.map((group) => {
              const names = group.productTypeIds.map(productName);
              const detail = `${names.slice(0, 2).join(', ')}${names.length > 2 ? ` +${names.length - 2}` : ''}`;
              const line = (
                label: string,
                field: 'portfolioLimit' | 'maxFunds' | 'weightsRule' | 'allowedFundGeoCategoryId',
              ) => (
                <TableRow key={`${group.id}-${field}`}>
                  <TableCell>{label}</TableCell>
                  {draft.plans.map((plan) => {
                    const key = ruleKey(plan.id, group.id);
                    const rule = draft.groupRules[key];

                    if (!group.active || !rule) {
                      return (
                        <React.Fragment key={plan.id}>
                          {valueCell(
                            group.active ? 'No rules · open' : 'Rules off · open',
                            COLORS.SECONDARY_GREEN,
                            false,
                            'groups',
                            group.id,
                          )}
                        </React.Fragment>
                      );
                    }

                    let text: string;

                    if (field === 'portfolioLimit') {
                      text = limitText(rule.portfolioLimit, 'portfolio');
                    } else if (field === 'maxFunds') {
                      text = limitText(rule.maxFunds, 'fund');
                    } else if (field === 'weightsRule') {
                      text = WEIGHTS_SHORT_LABELS[rule.weightsRule];
                    } else {
                      text = geoText(rule.allowedFundGeoCategoryId);
                    }

                    const color =
                      field === 'portfolioLimit' || field === 'maxFunds' ? valueColor(rule[field]) : COLORS.MAIN_DARK;

                    return (
                      <React.Fragment key={plan.id}>
                        {valueCell(text, color, changedKeys.has(`rule.${key}.${field}`), 'groups', group.id)}
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              );

              return (
                <React.Fragment key={group.id}>
                  {sectionRow(group.name, `${detail || 'no products'}${group.active ? '' : ' · rules off'}`)}
                  {line('Portfolios', 'portfolioLimit')}
                  {line('Funds in one portfolio', 'maxFunds')}
                  {line('Fund weights', 'weightsRule')}
                  {line('Funds from', 'allowedFundGeoCategoryId')}
                </React.Fragment>
              );
            })}

            {sectionRow('Services')}
            {services.map((service) => (
              <TableRow key={service.id}>
                <TableCell>{service.name}</TableCell>
                {draft.plans.map((plan) => {
                  const key = ruleKey(plan.id, service.id);
                  const included = Boolean(draft.serviceRules[key]);

                  return (
                    <React.Fragment key={plan.id}>
                      {valueCell(
                        included ? '✓ Included' : '🔒 Not included',
                        included ? COLORS.SECONDARY_GREEN : COLORS.LIGHT_GRAY,
                        changedKeys.has(`service.${key}`),
                        'services',
                      )}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', p: '12px 16px', color: COLORS.LIGHT_GRAY }}>
        <Typography variant="caption">* Saved recommendations count as portfolio slots</Typography>
        <Typography variant="caption" sx={{ color: COLORS.ORANGE }}>
          ● Changed, not published yet
        </Typography>
        <Typography variant="caption">🔒 Not allowed on this plan</Typography>
        <Typography variant="caption" sx={{ color: COLORS.SECONDARY_GREEN }}>
          ∞ Unlimited
        </Typography>
      </Box>
    </SectionCard>
  );
}
